import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./NavBar.css";
import { clearStoredData, getStoredData } from "../../api/fetchData.jsx";
import MobileNavBar from "./MobileNavBar";
import { useMediaQuery } from "react-responsive";

const FALLBACK_LOGO =
  "https://rohanlabourdashboard.axiomos.in/assets/logo-CwHNw-yK.png";

const FALLBACK_NAV_DATA = [
  {
    icon: "fa-play-circle",
    name: "Home",
    link: "/",
  },
  {
    icon: "fa-project-diagram",
    name: "Project Overview",
    link: "/project-dashboard",
  },
  {
    icon: "fa-bell",
    name: "Notifications",
    link: "/table/notifications",
  },
  {
    icon: "fa-folder-open",
    name: "File Manager",
    link: "/files",
  },
  {
    icon: "fa-chart-line",
    name: "Analytics Dashboard",
    link: "/dashboard",
  },
  {
    icon: "fa-user-circle",
    name: "My Profile",
    link: "/profile",
  },
  {
    icon: "fa-tachometer-alt",
    name: "Platform Management",
    link: "/management",
  },
  {
    icon: "fa-sliders-h",
    name: "System Settings",
    link: "/settings",
  },
  {
    icon: "fa-life-ring",
    name: "Support",
    link: "/support",
  },
  {
    icon: "fa-info-circle",
    name: "Help & Info",
    link: "/about",
  },
  {
    icon: "fa-sign-out-alt",
    name: "Log Out",
    onClick: "logout",
  },
];

const NavbarLoader = ({ sidebarWidth, isCompact }) => {
  return (
    <div
      className={`sidebar sidebar-loading ${isCompact ? "compact" : ""}`}
      style={{ width: sidebarWidth }}
    >
      <div className="navbar-loader-logo">
        <div className="navbar-loader-logo-mark" />
        {!isCompact && <div className="navbar-loader-logo-line" />}
      </div>

      <div className="navbar-loader-body">
        <div className="navbar-loader-spinner" />

        {!isCompact && (
          <div className="navbar-loader-text">
            <span>Loading workspace</span>
            <small>Preparing navigation...</small>
          </div>
        )}
      </div>

      <div className="navbar-loader-menu">
        {Array.from({ length: 7 }).map((_, index) => (
          <div className="navbar-loader-row" key={index}>
            <div className="navbar-loader-icon" />
            {!isCompact && <div className="navbar-loader-line" />}
          </div>
        ))}
      </div>
    </div>
  );
};

const MobileNavbarLoader = () => {
  return (
    <div className="mobile-navbar-loader">
      <div className="mobile-navbar-loader-logo" />
      <div className="mobile-navbar-loader-line" />
      <div className="mobile-navbar-loader-spinner" />
    </div>
  );
};

const Navbar = () => {
  const [navData, setNavData] = useState(FALLBACK_NAV_DATA);
  const [logo, setLogo] = useState(FALLBACK_LOGO);
  const [username, setUsername] = useState("Guest");
  const [loading, setLoading] = useState(true);
  const [logoFailed, setLogoFailed] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    return parseInt(localStorage.getItem("sidebarWidth"), 10) || 220;
  });

  const [isResizing, setIsResizing] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  const isCompact = sidebarWidth <= 140;
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery({ maxWidth: 786 });

  useEffect(() => {
    setHydrated(true);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const safeParseUser = () => {
      try {
        const rawUser = localStorage.getItem("userDetails");
        if (!rawUser) return "Guest";

        const user = JSON.parse(rawUser);
        return user?.name || "Guest";
      } catch {
        return "Guest";
      }
    };

    const loadNavbarData = async () => {
      try {
        const stored = await Promise.resolve(getStoredData());

        const storedArray = Array.isArray(stored)
          ? stored
          : stored
            ? [stored]
            : [];

        const navbarConfig = storedArray.find(
          (item) => item?.page === "navbar",
        );

        const menu =
          Array.isArray(navbarConfig?.menu) && navbarConfig.menu.length > 0
            ? navbarConfig.menu
            : FALLBACK_NAV_DATA;

        const finalLogo = navbarConfig?.CompanyLogo?.trim?.() || FALLBACK_LOGO;

        if (!cancelled) {
          setUsername(safeParseUser());
          setNavData(menu);
          setLogo(finalLogo);
          setLogoFailed(false);
          setLoading(false);
        }
      } catch (err) {
        console.warn("Navbar config unavailable. Using fallback navbar.");

        if (!cancelled) {
          setUsername(safeParseUser());
          setNavData(FALLBACK_NAV_DATA);
          setLogo(FALLBACK_LOGO);
          setLogoFailed(false);
          setLoading(false);
        }
      }
    };

    loadNavbarData();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const resize = (e) => {
      const newWidth = Math.min(Math.max(e.clientX, 80), 230);
      setSidebarWidth(newWidth);
      localStorage.setItem("sidebarWidth", String(newWidth));
    };

    const stopResize = () => {
      setIsResizing(false);
      document.body.classList.remove("navbar-resizing");
    };

    window.addEventListener("mousemove", resize);
    window.addEventListener("mouseup", stopResize);

    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResize);
      document.body.classList.remove("navbar-resizing");
    };
  }, [isResizing]);

  const startResize = () => {
    setIsResizing(true);
    document.body.classList.add("navbar-resizing");
  };

const logout = () => {
  if (isLoggingOut) return;

  setIsLoggingOut(true);

  try {
    // Explicitly clear API data and metadata.
    clearStoredData();

    // Clear all local application data, including:
    // userDetails, tokens, cached API data and sidebar preferences.
    localStorage.clear();

    // Clear temporary browser-session data.
    sessionStorage.clear();

    // Remove any cookies accessible through JavaScript.
    document.cookie.split(";").forEach((cookie) => {
      const cookieName = cookie.split("=")[0]?.trim();

      if (cookieName) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
    });

    /*
     * Force a complete page navigation.
     *
     * This clears:
     * - React component state
     * - active module variables
     * - navbar state
     * - in-memory API data
     * - browser history entry for the protected page
     */
    window.location.replace("/login");
  } catch (error) {
    console.error("Logout failed:", error);

    /*
     * Even if storage cleanup throws an error,
     * still force the user back to login.
     */
    window.location.replace("/login");
  }
};

  const goToProfile = () => navigate("/profile");

  const actionMap = {
    logout,
    profile: goToProfile,
  };

const handleMenuClick = (item) => {
  if (isLoggingOut) return;

  if (item.onClick && actionMap[item.onClick]) {
    actionMap[item.onClick]();
    return;
  }

  if (item.link?.startsWith("http")) {
    window.open(item.link, "_blank", "noopener,noreferrer");
    return;
  }

  if (item.link) {
    navigate(item.link);
  }
};

  if (!hydrated || loading) {
    if (isMobile) return <MobileNavbarLoader />;

    return <NavbarLoader sidebarWidth={sidebarWidth} isCompact={isCompact} />;
  }

  if (isMobile) {
    return (
      <MobileNavBar
        navData={navData}
        logo={logoFailed ? FALLBACK_LOGO : logo}
        username={username}
        onLogout={logout}
        onProfile={goToProfile}
      />
    );
  }

  return (
    <div
      className={`sidebar ${isCompact ? "compact" : ""}`}
      style={{ width: sidebarWidth }}
    >
      {!logoFailed ? (
        <img
          src={logo || FALLBACK_LOGO}
          alt="JKumar SureSafe Logo"
          className="navbar-logo"
          onError={() => {
            if (logo !== FALLBACK_LOGO) {
              setLogo(FALLBACK_LOGO);
            } else {
              setLogoFailed(true);
            }
          }}
        />
      ) : (
        <div className="navbar-logo-fallback">
          <span>JK</span>
        </div>
      )}

      <div className="sidebar-scroll">
        <ul className="sidebar-menu">
          {navData.map((item, idx) => {
            const isActive =
              item.link &&
              (location.pathname === item.link ||
                (item.link !== "/" &&
                  item.link !== "/home" &&
                  location.pathname.startsWith(item.link)));

            return (
              <li key={`${item.name || "menu"}-${idx}`}>
                <button
                  type="button"
                  className={`sidebar-item ${isActive ? "active" : ""}`}
                  onClick={() => handleMenuClick(item)}
                  title={isCompact ? item.name : ""}
                  disabled={isLoggingOut}
                >
                  {item.icon?.startsWith("fa-") ? (
                    <i className={`fas ${item.icon} menu-icon`} />
                  ) : item.icon ? (
                    <img src={item.icon} alt="" className="menu-icon" />
                  ) : (
                    <span className="menu-icon menu-icon-fallback">
                      {item.name?.charAt(0) || "•"}
                    </span>
                  )}

                  <span className="menu-text">{item.name}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div
        className="resize-handle"
        onMouseDown={startResize}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
      />
    </div>
  );
};

export default Navbar;
